import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaSave } from 'react-icons/fa';
import Swal from 'sweetalert2';
import AppContext from '../../../context/AppContext';
const AdminProfileSettings = () => {
  const { baseUrl, authToken, validateField } = useContext(AppContext);

  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: '',
    joinedDate: null
  });

  const [formData, setFormData] = useState({ name: '' });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  useEffect(() => {
    fetch(`${baseUrl}/admin/get-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`
      }
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          const admin = result.data;
          setProfile({
            name: admin.admusername || '',
            email: admin.admemail || '',
            role: admin.admrole || '',
            joinedDate: admin.created_at || null
          });
          setFormData({ name: admin.admusername || '' });
        }
      })
      .catch((err) => {
        console.error('Failed to fetch admin profile:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [baseUrl, authToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const nameError = validateField('name', formData.name);
    if (nameError) newErrors.name = nameError;
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsUpdatingProfile(true);

    fetch(`${baseUrl}/admin/update-admin`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${authToken}`
      },
      body: JSON.stringify({ name: formData.name })
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          setProfile((prev) => ({ ...prev, name: formData.name }));
          Swal.fire({
            icon: 'success',
            title: 'Updated Successfully',
            text: 'Your profile has been updated.',
            confirmButtonColor: '#2C5F7C'
          });
        } else {
          setErrors({ name: result.message || 'Update failed' });
          Swal.fire({
            icon: 'error',
            title: 'Update Failed',
            text: result.message || 'Something went wrong. Please try again.'
          });
        }
      })
      .catch((err) => {
        console.error('Failed to update admin profile:', err);
        setErrors({ name: 'Something went wrong. Please try again.' });
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Something went wrong. Please try again.'
        });
      })
      .finally(() => {
        setIsUpdatingProfile(false);
      });
  };

  const getInitial = () => {
    if (formData.name && formData.name.trim()) {
      return formData.name.trim().charAt(0).toUpperCase();
    }
    return 'A';
  };

  if (isLoading) {
    return (
      <Card className="adm-settings-card">
        <Card.Body className="adm-settings-loading">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <style>{`
        .adm-settings-avatar-section .adm-settings-avatar-initial {
          width: 80px !important;
          height: 80px !important;
          min-width: 80px !important;
          min-height: 80px !important;
          border-radius: 50% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: linear-gradient(135deg, #2C5F7C 0%, #4A9E8E 100%) !important;
          color: #ffffff !important;
          font-size: 2rem !important;
          font-weight: 600 !important;
          flex-shrink: 0 !important;
          line-height: 1 !important;
        }
      `}</style>
      <Card className="adm-settings-card">
      <Card.Header className="adm-settings-card-header">
        <h3><FaUser className="me-2" />Profile Information</h3>
        <p className="adm-settings-card-subtitle">Update your personal details</p>
      </Card.Header>

      <Card.Body>
        <Form onSubmit={handleSubmit}>
          <div className="adm-settings-avatar-section">
            <div className="adm-settings-avatar-initial">
              {getInitial()}
            </div>
            <div className="adm-settings-avatar-info">
              <h5>{profile.name}</h5>
              <p className="text-muted">{profile.role}</p>
              {profile.joinedDate && (
                <small className="text-muted">
                  Member since {new Date(profile.joinedDate).toLocaleDateString()}
                </small>
              )}
            </div>
          </div>

          <hr className="my-4" />

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaUser className="me-2" />Full Name *
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  isInvalid={!!errors.name}
                  className="adm-settings-input"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaEnvelope className="me-2" />Email Address
                </Form.Label>
                <Form.Control
                  type="email"
                  value={profile.email}
                  disabled
                  className="adm-settings-input"
                />
                <Form.Text className="text-muted">
                  Email cannot be changed
                </Form.Text>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Role</Form.Label>
                <Form.Control
                  type="text"
                  value={profile.role}
                  disabled
                  className="adm-settings-input"
                />
                <Form.Text className="text-muted">
                  Contact system administrator to change role
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Submit Button */}
          <div className="adm-settings-form-footer">
            <Button
              variant="primary"
              type="submit"
              disabled={isUpdatingProfile}
              className="adm-settings-btn-primary"
            >
              {isUpdatingProfile ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    className="me-2"
                  />
                  Updating...
                </>
              ) : (
                <>
                  <FaSave className="me-2" />
                  Update Profile
                </>
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
    </Card>
    </>
  );
};

export default AdminProfileSettings;